import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Shown above all authenticated pages.
 *  - Trialing with <=10 days left → friendly indigo nudge
 *  - Trial expired / canceled / past_due → red lockout banner
 *  - Active or admin → nothing
 */
export const TrialStatusBanner = () => {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    if (!user || user.role === 'admin') return;
    axios.get(`${API_URL}/api/billing/status`, { withCredentials: true })
      .then(r => setBilling(r.data))
      .catch(() => {});
  }, [user]);

  if (!user || user.role === 'admin' || !billing) return null;

  // Hide on the billing page itself
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/billing')) return null;

  const expired = !billing.active;
  const trialing = billing.active && billing.status === 'trialing';
  const daysLeft = billing.trial_days_remaining;
  const nudge = trialing && daysLeft <= 10;

  if (expired) {
    return (
      <div data-testid="paywall-banner" className="bg-red-50 border-b border-red-200 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-red-900">
          <AlertTriangle size={16} className="text-red-600 flex-shrink-0" />
          <span><strong>Your trial has ended.</strong> Read-only mode is on. Subscribe to restore full access.</span>
        </div>
        <Link
          to="/billing"
          data-testid="paywall-subscribe-link"
          className="text-xs font-semibold bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md transition-colors"
        >
          Subscribe for $4/mo
        </Link>
      </div>
    );
  }

  if (nudge) {
    return (
      <div data-testid="trial-nudge-banner" className="bg-indigo-50 border-b border-indigo-200 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-indigo-900">
          <Sparkles size={16} className="text-indigo-600 flex-shrink-0" />
          <span><strong>{daysLeft} day{daysLeft === 1 ? '' : 's'} left</strong> in your free trial. Keep your data flowing — subscribe anytime.</span>
        </div>
        <Link
          to="/billing"
          data-testid="trial-subscribe-link"
          className="text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md transition-colors"
        >
          Subscribe for $4/mo
        </Link>
      </div>
    );
  }

  return null;
};
